import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Ban, CheckCircle2, MessageSquareOff, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'

/**
 * Opt-out por palabra clave (Sprint 19).
 *
 * Configura las keywords de baja/alta/ayuda (STOP/BAJA/ALTA/AYUDA) que se
 * detectan en los inbound de SMS y WhatsApp, los canales a los que aplica, y
 * los textos de las confirmaciones transaccionales. Además muestra el
 * histórico de bajas recibidas por keyword (dirección enmascarada).
 *
 * Cableada en /marketing/opt-out-keywords (App.tsx) + nav «Opt-out STOP»
 * (AppShell.tsx).
 */
const CHANNELS = ['SMS', 'WHATSAPP', 'VOICE', 'EMAIL', 'CALL'] as const
type Channel = (typeof CHANNELS)[number]

// Canales ofrecidos en la UI (los realmente soportados por keyword hoy).
const UI_CHANNELS: { value: Channel; label: string }[] = [
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
]

interface Config {
  id: string
  stopKeywords: string[]
  startKeywords: string[]
  helpKeywords: string[]
  appliesToChannels: Channel[]
  autoReplyEnabled: boolean
  stopReplyText: string
  startReplyText: string
  helpReplyText: string
}

interface EventRow {
  id: string
  occurredAt: string
  channel: string
  keyword: string | null
  address: string
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Lista separada por comas ⇄ array de strings (trim, sin vacíos). */
function parseList(csv: string): string[] {
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export function OptOutKeywordsPage() {
  const [config, setConfig] = useState<Config | null>(null)
  const [events, setEvents] = useState<EventRow[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  // Draft editable (strings para inputs de lista por comas).
  const [stopKw, setStopKw] = useState('')
  const [startKw, setStartKw] = useState('')
  const [helpKw, setHelpKw] = useState('')
  const [channels, setChannels] = useState<Channel[]>([])
  const [autoReply, setAutoReply] = useState(true)
  const [stopText, setStopText] = useState('')
  const [startText, setStartText] = useState('')
  const [helpText, setHelpText] = useState('')

  const hydrate = useCallback((c: Config) => {
    setConfig(c)
    setStopKw(c.stopKeywords.join(', '))
    setStartKw(c.startKeywords.join(', '))
    setHelpKw(c.helpKeywords.join(', '))
    setChannels(c.appliesToChannels)
    setAutoReply(c.autoReplyEnabled)
    setStopText(c.stopReplyText)
    setStartText(c.startReplyText)
    setHelpText(c.helpReplyText)
  }, [])

  const load = useCallback(async () => {
    setErr(null)
    try {
      const [c, ev] = await Promise.all([
        api<Config>('/v1/marketing/opt-out-keywords'),
        api<{ items: EventRow[] }>('/v1/marketing/opt-out-keywords/events?limit=100'),
      ])
      hydrate(c)
      setEvents(ev.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [hydrate])

  useEffect(() => {
    void load()
  }, [load])

  function toggleChannel(ch: Channel) {
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]))
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setOkMsg(null)
    setErr(null)
    const stopList = parseList(stopKw)
    if (stopList.length === 0) {
      setErr('Debes definir al menos una palabra clave de baja (STOP).')
      return
    }
    setSaving(true)
    try {
      const updated = await api<Config>('/v1/marketing/opt-out-keywords', {
        method: 'PUT',
        body: JSON.stringify({
          stopKeywords: stopList,
          startKeywords: parseList(startKw),
          helpKeywords: parseList(helpKw),
          appliesToChannels: channels,
          autoReplyEnabled: autoReply,
          stopReplyText: stopText.trim(),
          startReplyText: startText.trim(),
          helpReplyText: helpText.trim(),
        }),
      })
      hydrate(updated)
      setOkMsg('Configuración guardada.')
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-5xl">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <MessageSquareOff className="size-5 text-blueprint-700" aria-hidden="true" />
            Opt-out por palabra clave
          </h1>
          <p className="text-xs font-mono text-ink-700/60 mt-1 max-w-2xl">
            Cuando un cliente responde STOP/BAJA por SMS o WhatsApp, se le da de baja
            automáticamente y se le envía una confirmación. Espejo por teléfono de la baja
            LSSI de email. STOP y AYUDA son obligatorios por los operadores de SMS.
          </p>
        </div>
        <button type="button" onClick={() => void load()} className="bp-btn shrink-0" title="Recargar">
          <RefreshCw className="size-3" aria-hidden="true" />
          Recargar
        </button>
      </header>

      {err && (
        <div
          role="alert"
          className="flex items-start gap-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-4"
        >
          <AlertTriangle className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{err}</span>
        </div>
      )}
      {okMsg && (
        <div className="flex items-center gap-2 text-xs font-mono text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5 mb-4">
          <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />
          <span>{okMsg}</span>
        </div>
      )}

      {config === null && !err ? (
        <div className="bp-card p-6 text-xs font-mono text-ink-700/50" role="status">
          Cargando configuración…
        </div>
      ) : (
        <form onSubmit={(e) => void save(e)} className="bp-card p-4 mb-6 grid gap-4">
          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="stopKw">
              Palabras de baja (STOP) · separadas por comas
            </label>
            <input
              id="stopKw"
              data-testid="optout-stop-keywords"
              value={stopKw}
              onChange={(e) => setStopKw(e.target.value)}
              placeholder="STOP, BAJA, CANCELAR"
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="startKw">
              Palabras de alta (START) · separadas por comas
            </label>
            <input
              id="startKw"
              data-testid="optout-start-keywords"
              value={startKw}
              onChange={(e) => setStartKw(e.target.value)}
              placeholder="ALTA, START, SUSCRIBIR"
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
            <span className="text-[10px] font-mono text-ink-700/50">
              El alta levanta la supresión de este canal, pero NO concede consentimiento de
              marketing (eso requiere doble opt-in).
            </span>
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="helpKw">
              Palabras de ayuda (HELP) · separadas por comas
            </label>
            <input
              id="helpKw"
              data-testid="optout-help-keywords"
              value={helpKw}
              onChange={(e) => setHelpKw(e.target.value)}
              placeholder="AYUDA, HELP, INFO"
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-ink-900">Canales donde aplica</span>
            <div className="flex flex-wrap gap-3">
              {UI_CHANNELS.map((ch) => (
                <label key={ch.value} className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`optout-channel-${ch.value}`}
                    checked={channels.includes(ch.value)}
                    onChange={() => toggleChannel(ch.value)}
                  />
                  {ch.label}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-1.5">
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                data-testid="optout-autoreply"
                checked={autoReply}
                onChange={(e) => setAutoReply(e.target.checked)}
              />
              <span className="font-medium text-ink-900">Enviar respuesta automática</span>
            </label>
            <span className="text-[10px] font-mono text-ink-700/50">
              La confirmación de baja es un mensaje transaccional obligatorio: sale aunque la
              dirección quede suprimida para marketing.
            </span>
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="stopText">
              Texto de confirmación de baja (STOP)
            </label>
            <textarea
              id="stopText"
              data-testid="optout-stop-text"
              value={stopText}
              onChange={(e) => setStopText(e.target.value)}
              rows={3}
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="startText">
              Texto de confirmación de alta (START)
            </label>
            <textarea
              id="startText"
              data-testid="optout-start-text"
              value={startText}
              onChange={(e) => setStartText(e.target.value)}
              rows={3}
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-medium text-ink-900" htmlFor="helpText">
              Texto de ayuda (HELP)
            </label>
            <textarea
              id="helpText"
              data-testid="optout-help-text"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              rows={3}
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div>
            <button
              type="submit"
              data-testid="optout-save"
              disabled={saving}
              aria-busy={saving}
              className="bp-btn-primary"
            >
              <CheckCircle2 className="size-3.5" aria-hidden="true" />
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </button>
          </div>
        </form>
      )}

      <section>
        <h2 className="text-sm font-medium text-ink-900 flex items-center gap-2 mb-2">
          <Ban className="size-4 text-ink-700/60" aria-hidden="true" />
          Bajas recientes por palabra clave
        </h2>
        <div className="bp-card overflow-x-auto">
          <table className="w-full text-xs" data-testid="optout-events">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-700/60 border-b border-ink-900/10">
                <th className="px-3 py-2 font-medium">Fecha</th>
                <th className="px-3 py-2 font-medium">Canal</th>
                <th className="px-3 py-2 font-medium">Palabra clave</th>
                <th className="px-3 py-2 font-medium">Dirección</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-b border-ink-900/5 last:border-0">
                  <td className="px-3 py-2 text-ink-700/80">{fmtDate(ev.occurredAt)}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{ev.channel}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-ink-900">{ev.keyword ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-ink-700/80">{ev.address}</td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-ink-700/50">
                    Sin bajas por palabra clave todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

export default OptOutKeywordsPage
